let currencySymbol = '$';
if (theme.settings.storeCurrency && Shopify) {
  const { currency: { active } } = Shopify;
  const { storeCurrency } = theme.settings;
  const current = storeCurrency.find(({ ISO }) => ISO === active );
  if (current) currencySymbol = current.symbol;
}

const moneyFormat = (amount) => {
  return `${currencySymbol}${(amount / 100).toFixed(2)}`;
}

const timeout = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getPromoDotPositions = (count) => {
  if (!count || count <= 0) return [];
  if (count === 1) return [100];
  if (count === 2) return [50, 100];
  if (count === 3) return [50, 75, 100];
  if (count === 4) return [25, 50, 75, 100];
  if (count === 5) return [20, 40, 60, 80, 100];

  const step = 100 / count;
  return Array.from({ length: count }, (_, index) => step * (index + 1));
};

const getProgressPosition = (total, targetValues, positions) => {
  if (!targetValues || !targetValues.length) return 0;
  if (total <= 0) return 0;

  const lastIndex = targetValues.length - 1;
  if (total >= targetValues[lastIndex]) return 100;

  for (let i = 0; i < targetValues.length; i++) {
    if (total <= targetValues[i]) {
      const prevValue = i === 0 ? 0 : targetValues[i - 1];
      const prevPos = i === 0 ? 0 : positions[i - 1];
      const nextPos = positions[i];
      const range = targetValues[i] - prevValue;
      const ratio = range > 0 ? (total - prevValue) / range : 1;
      return prevPos + (nextPos - prevPos) * ratio;
    }
  }

  return 100;
};

const fetchCart = async () => {
  try {
    const res = await fetch('/cart.js');
    const data = await res.json();
    if (res.ok) return data;
  } catch (error) {
    console.log(error);
    return null
  }
}

class CartThresholdFST extends HTMLElement {
  constructor() {
    super();

    this.enabledTargets = [];
    this.shippingTargets = [];
    this.gwpTargets = [];
    this.discountTargets = [];
    this.maxTarget = 0;

    this.script = this.querySelector('script');
    this.data = this.script ? JSON.parse(this.script.textContent) : null;
    if (!this.data) return;

    const settings = this.data.settings || {};
    this.shippingEnabled = settings.shipping_enabled ?? settings.enabled ?? false;
    this.gwpEnabled = settings.gwp_enabled ?? false;
    this.discountEnabled = settings.discount_enabled ?? false;
    this.defaultMessage = settings.default ?? '';
    this.highestOnly = settings.highest_only ?? settings.highest ?? false;

    const shippingTargetsRaw = Array.isArray(this.data.shippingTargets)
      ? this.data.shippingTargets
      : Array.isArray(this.data.targets)
        ? this.data.targets
        : [];
    const gwpTargetsRaw = Array.isArray(this.data.gwpTargets) ? this.data.gwpTargets : [];
    const discountTargetsRaw = Array.isArray(this.data.discountTargets) ? this.data.discountTargets : [];

    shippingTargetsRaw.forEach((target) => this.#addTarget(target, 'shipping'));
    gwpTargetsRaw.forEach((target) => this.#addTarget(target, 'gwp'));
    discountTargetsRaw.forEach((target) => this.#addTarget(target, 'discount'));

    if (!this.enabledTargets.length) return;

    this.enabledTargets.sort((a, b) => a.value - b.value);
    this.enabledTargets.forEach((target, index) => {
      target.index = index;
    });

    this.maxTarget = this.enabledTargets[this.enabledTargets.length - 1]?.value ?? 0;
    if (this.maxTarget === 0) return;

    this.#renderElements();
    this.#init();

    document.addEventListener('cart:updated', (evt) => {
      const { detail: { cart } } = evt;
      this.#calculateShipping(cart);
    });
  }

  #addTarget(target, type) {
    if (!target) return;
    if (type === 'shipping' && !this.shippingEnabled) return;
    if (type === 'gwp' && !this.gwpEnabled) return;
    if (type === 'discount' && !this.discountEnabled) return;

    const value = Number(target.value ?? 0);
    if (Number.isNaN(value) || value <= 0) return;

    const normalisedTarget = {
      ...target,
      value,
      type,
      messaging: target.messaging || {}
    };

    this.enabledTargets.push(normalisedTarget);
    if (type === 'shipping') this.shippingTargets.push(normalisedTarget);
    if (type === 'gwp') this.gwpTargets.push(normalisedTarget);
    if (type === 'discount') this.discountTargets.push(normalisedTarget);
  }

  #convertToCurrency(amount) {
    return Shopify?.currency?.rate ? Math.trunc(Shopify.currency.rate * +amount) : Math.trunc(+amount);
  }

  async #init() {
    const cartData = await fetchCart();
    this.#calculateShipping(cartData);
  }

  #renderElements() {
    const initialTarget = this.#convertToCurrency(this.maxTarget);
    const renderProgressDots = this.enabledTargets.map(({ name, label, value, index, type }) => {
      return `
        <span
          class="promo-dots promo--${name} promo--${name}-${index}"
          data-target="${value}"
          data-type="${type}"
          data-currency-symbol="${currencySymbol}"
          data-label="${label}"
          aria-hidden="true"
          style="--position: 0">
        </span>
      `;
    }).join('');

    const html = `
      <div id="cart-promotions-message-success-fst" class="cart-promotions-message-success appear-animation appear-delay-1" style="display: none;"></div>
      <div id="cart-promotions-message-fst" class="cart-promotions-message appear-animation appear-delay-1"></div>
      <div class="cart-promotions-wrapper cart-promotions-wrapper-fst appear-animation appear-delay-1" data-target="${initialTarget}" data-currency-symbol="${currencySymbol}">
        <label for="cart-promotions" class="visually-hidden">Cart Promotions</label>
        <progress id="cart-promotions-fst" max="100" value="0" data-target="${initialTarget}"></progress>
        <span class="promo-dots promo--value"></span>
        ${renderProgressDots}
      </div>
    `;

    this.innerHTML = html;
  }

  #collectTotals(cartJson) {
    if (!cartJson) {
      return {
        base: 0,
        shipping: 0,
        gwp: 0,
        hideShipping: false
      };
    }

    let total = 0;
    if (cartJson.total_price && cartJson.total_price > 0) {
      total = cartJson.total_price;
    } else if (cartJson.final_price && cartJson.final_price > 0) {
      total = cartJson.final_price;
    }

    let dropshipTotalPrice = 0;
    let ignoreGWPTotal = 0;
    let hideShipping = false;

    (cartJson.items || []).forEach((item) => {
      const properties = item.properties || {};
      const propertyKeys = Object.keys(properties);
      if (propertyKeys.includes('_no-free-shipping')) {
        hideShipping = true;
      }
      if (propertyKeys.includes('_dropship')) {
        dropshipTotalPrice += item.final_line_price;
      }
      if (propertyKeys.includes('_ignore-gwp')) {
        ignoreGWPTotal += item.final_line_price;
      }
    });

    const baseTotal = total > 0 ? total / 100 : 0;
    const shippingTotal = total > 0 ? Math.max((total - dropshipTotalPrice) / 100, 0) : 0;
    const gwpTotal = total > 0 ? Math.max((total - ignoreGWPTotal) / 100, 0) : 0;

    return {
      base: baseTotal,
      shipping: shippingTotal,
      gwp: gwpTotal,
      hideShipping
    };
  }

  #getEligibleTotal(totals, type) {
    if (!totals) return 0;
    if (type === 'shipping') return totals.shipping;
    if (type === 'gwp') return totals.gwp;
    if (type === 'discount') return totals.base;
    return totals.base;
  }

  #updatePromoDots(activeTargets) {
    const activeSet = new Set(activeTargets);
    const positions = getPromoDotPositions(activeTargets.length);

    this.enabledTargets.forEach((target) => {
      const dot = this.querySelector(`.promo--${target.name}-${target.index}`);
      if (!dot) return;

      if (activeSet.has(target)) {
        const targetIndex = activeTargets.indexOf(target);
        const position = positions[targetIndex] ?? 0;
        dot.removeAttribute('hidden');
        // Restore original label if dot becomes visible but not yet active
        if (!dot.classList.contains('active')) {
          dot.setAttribute('data-label', target.label || '');
        }
        dot.style.setProperty('--position', position);
      } else {
        dot.setAttribute('hidden', 'hidden');
        dot.classList.remove('active');
        dot.style.setProperty('--position', 0);
        // Restore original label when hidden
        dot.setAttribute('data-label', target.label || '');
      }
    });
  }

  #calculateShipping(cartJson) {
    const totals = this.#collectTotals(cartJson);
    const container = this.querySelector('#cart-promotions-fst');
    const outerContainer = this.querySelector('.cart-promotions-wrapper-fst');
    const valueDot = this.querySelector('.promo--value');
    const messageWrapper = this.querySelector('#cart-promotions-message-fst');
    const successMessageWrapper = this.querySelector('#cart-promotions-message-success-fst');

    if (!container || !outerContainer || !messageWrapper || !successMessageWrapper) {
      this.#updateShippingSavingsList(totals);
      return;
    }

    const activeTargets = this.enabledTargets.filter((target) => !(target.type === 'shipping' && totals.hideShipping));

    if (!activeTargets.length) {
      container.setAttribute('value', 0);
      outerContainer.setAttribute('value', 0);
      outerContainer.classList.add('hide');
      messageWrapper.innerHTML = '';
      messageWrapper.classList.remove('show');
      successMessageWrapper.innerHTML = '';
      successMessageWrapper.classList.remove('show');
      this.#updateShippingSavingsList(totals);
      return;
    }

    const highestTarget = activeTargets[activeTargets.length - 1];
    const maxTargetValue = this.#convertToCurrency(highestTarget.value);
    const progressBaseTotal = this.#getEligibleTotal(totals, highestTarget.type);
    const targetValues = activeTargets.map((target) => this.#convertToCurrency(target.value));
    const positions = getPromoDotPositions(activeTargets.length);
    const progressValueRaw = getProgressPosition(progressBaseTotal, targetValues, positions);
    const progressValue = Math.max(0, Math.min(progressValueRaw, 100));

    container.setAttribute('value', progressValue ? progressValue : 0);
    outerContainer.setAttribute('value', progressValue ? progressValue : 0);
    outerContainer.dataset.target = maxTargetValue;
    container.dataset.target = maxTargetValue;

    if (valueDot) {
      valueDot.style.setProperty('--position', progressValue > 100 ? 100 : progressValue);
    }

    this.#updatePromoDots(activeTargets);

    let message = '';
    const successMessages = [];
    let allPromosUnlocked = true;

    for (const target of activeTargets) {
      const promoDot = this.querySelector(`.promo--${target.name}-${target.index}`);
      const eligibleTotal = this.#getEligibleTotal(totals, target.type);
      const targetValueConverted = this.#convertToCurrency(target.value);
      const remaining = targetValueConverted - eligibleTotal;

      if (remaining <= 0) {
        if (promoDot) {
          promoDot.classList.add('active');
         // promoDot.setAttribute('data-label', 'Unlocked');
        }
        if (target.messaging?.success) {
          let successMsg = target.messaging.success.replaceAll('[title]', target.title ?? '');
          if (target.type === 'discount' && target.saveAmount) {
            const formattedSave = `${currencySymbol}${target.saveAmount.toFixed(2)}`;
            successMsg = successMsg.replace('[save]', formattedSave);
          }
          successMessages.push(`<div class="cart-promotion-success-item">${successMsg}</div>`);
        }
      } else {
        allPromosUnlocked = false;
        if (promoDot) {
          promoDot.classList.remove('active');
          // Restore original label when not active
          const originalLabel = target.label || '';
          promoDot.setAttribute('data-label', originalLabel);
        }

        if (!message && target.messaging?.ongoing) {
          const formattedRemaining = `${currencySymbol}${remaining ? remaining.toFixed(2) : target.value.toFixed(2)}`;
          let ongoingMsg = target.messaging.ongoing
            .replace('[remaining]', formattedRemaining)
            .replaceAll('[title]', target.title ?? '');
          if (target.type === 'discount' && target.saveAmount) {
            const formattedSave = `${currencySymbol}${target.saveAmount.toFixed(2)}`;
            ongoingMsg = ongoingMsg.replace('[save]', formattedSave);
          }
          message = ongoingMsg;
        }
      }
    }

    // Check if all active targets are unlocked
    if (allPromosUnlocked && activeTargets.length > 0) {
      message = 'All Promos Unlocked';
      outerContainer.classList.remove('hide');
    } else if (progressValue >= 100 && !allPromosUnlocked) {
      outerContainer.classList.add('hide');
    } else {
      outerContainer.classList.remove('hide');
    }

    if (!message && !allPromosUnlocked) {
      const firstTarget = activeTargets[0];
      if (firstTarget?.type === 'shipping' && this.defaultMessage && this.#getEligibleTotal(totals, 'shipping') <= 0) {
        message = this.defaultMessage;
      }
    }

    const successMessageContent = successMessages.join('');

    messageWrapper.innerHTML = message;
    messageWrapper.classList.toggle('show', Boolean(message));

    // Hide success message wrapper temporarily
    successMessageWrapper.innerHTML = successMessageContent;
    successMessageWrapper.style.display = 'none';
    successMessageWrapper.classList.remove('show');

    this.#updateShippingSavingsList(totals);
  }

  #updateShippingSavingsList(totals) {
    const lists = document.querySelectorAll('[data-shipping-savings-list]');
    if (!lists.length) return;

    if (!this.shippingEnabled || !this.shippingTargets.length) {
      lists.forEach((el) => {
        el.innerHTML = '';
      });
      document.querySelectorAll('[data-cart-shipping-savings]').forEach((sec) => {
        sec.setAttribute('hidden', '');
      });
      return;
    }

    const hideShipping = totals.hideShipping;
    const eligibleTotal = this.#getEligibleTotal(totals, 'shipping');
    const lines = [];

    if (!hideShipping) {
      const sorted = [...this.shippingTargets].sort((a, b) => a.value - b.value);
      for (const target of sorted) {
        const targetValueConverted = this.#convertToCurrency(target.value);
        const remaining = targetValueConverted - eligibleTotal;
        if (remaining > 0) continue;

        const title = target.name === 'free-express-shipping' ? 'Express Shipping' : 'Shipping';
        lines.push(`<div class="cart__item-sub cart__item-row cart-drawer__discount-line cart-drawer__shipping-savings-line"><span class="cart-drawer__discount-code-name">${title}</span><span class="cart__discount cart-drawer__discount-code-amount">Free</span></div>`);
      }
    }

    const html = lines.join('');
    lists.forEach((el) => {
      el.innerHTML = html;
    });
    document.querySelectorAll('[data-cart-shipping-savings]').forEach((sec) => {
      if (lines.length) sec.removeAttribute('hidden');
      else sec.setAttribute('hidden', '');
    });
  }
}

customElements.define('widget-cart-threshold-fst', CartThresholdFST);

class CartThresholdPromoTiers extends HTMLElement {
  constructor() {
    super();

    this.enabledTargets = [];
    this.maxTarget = 0;

    this.script = this.querySelector('script');
    this.data = this.script ? JSON.parse(this.script.textContent) : null;
    this.enabled = this.data ? this.data.settings.enabled : null;
    this.highest = this.data ? this.data.settings.highest : null;
    if (!this.data || !this.enabled) return;

    const [ tier1, tier2, tier3 ] = this.data.targets;
    if (tier1.value > 0) {
      this.enabledTargets.push(tier1);
    }
    if (tier2.value > 0) {
      this.enabledTargets.push(tier2);
    }
    if (tier3.value > 0) {
      this.enabledTargets.push(tier3);
    }

    if (!this.enabledTargets.length) return;
    this.enabledTargets = this.enabledTargets.sort((a, b) => a.value - b.value);

    const allTargets = this.enabledTargets.map(({ value }) => value);
    this.maxTarget = Math.max(...allTargets);
    if (this.maxTarget === 0) return;

    this.#renderElements();
    this.#init();

    document.addEventListener('cart:updated', (evt) => {
      const { detail: { cart } } = evt;
      this.#calculateShipping(cart);
    });
  }

  #convertToCurrency(amount) {
    return Shopify?.currency?.rate ? Math.trunc(Shopify.currency.rate * +amount) : Math.trunc(+amount)
  }

  async #init() {
    const cartData = await fetchCart();
    this.#calculateShipping(cartData);
  }

  #renderElements() {
    const target= this.#convertToCurrency(this.maxTarget);
    const positions = getPromoDotPositions(this.enabledTargets.length);
    const renderProgressDots = this.enabledTargets.map(({ name, label, value }, index) => {
      return `
        <span
          class="promo-dots promo--${name} promo--${name}-${index}"
          data-target="${value}"
          data-currency-symbol="${currencySymbol}"
          data-label="${label}"
          aria-hidden="true"
          style="--position: ${positions[index] ?? 0}">
        </span>
      `
    }).join('');

    const html = `
      <div id="cart-promotions-message-success-tiers" class="cart-promotions-message-success appear-animation appear-delay-1"></div>
      <div id="cart-promotions-message-tiers" class="cart-promotions-message appear-animation appear-delay-1"></div>
      <div class="cart-promotions-wrapper cart-promotions-wrapper-tiers appear-animation appear-delay-1" data-target="${target}" data-currency-symbol="${currencySymbol}">
        <label for="cart-promotions" class="visually-hidden">Cart Promotions - Free Gift</label>
        <progress id="cart-promotions-tiers" max="100" value="0" data-target="${target}"></progress>
        <span class="promo-dots promo--value"></span>
        ${renderProgressDots}
      </div>
    `;

    this.innerHTML = html;
  }

  #calculateShipping(cartJson) {
  
    const targets = this.enabledTargets;
    let total = cartJson.total_price && cartJson.total_price > 0 ? cartJson.total_price : cartJson.final_price && cartJson.final_price > 0 ? cartJson.final_price : 0;
    let ignoreGWPTotal = 0;
    cartJson.items.forEach(item => {
      if (item.properties?.["_ignore-gwp"]) {
        ignoreGWPTotal += item.final_line_price;
      }
    })
    const cartTotal = total > 0 ? (total - ignoreGWPTotal) / 100 : 0;

    const targetValues = this.enabledTargets.map((target) => this.#convertToCurrency(target.value));
    const positions = getPromoDotPositions(this.enabledTargets.length);
    const progressValue = getProgressPosition(cartTotal, targetValues, positions);	
    const container = document.getElementById('cart-promotions-tiers');	
    const outerContainer = document.querySelector('.cart-promotions-wrapper-tiers');

    let message = '';
    let messageSuccess = '';

    if (progressValue >= 100) {	
      container.setAttribute('value', 100);	
      container.parentElement.setAttribute('value', 100);	
      outerContainer.classList.add('hide');	
    } else {	
      container.setAttribute('value', progressValue ? progressValue : 0);	
      container.parentElement.setAttribute('value', progressValue ? progressValue : 0);	
      outerContainer.classList.remove('hide');	
    }

    if (this.querySelector('.promo--value')) {	
      this.querySelector('.promo--value').style.setProperty('--position', progressValue > 100 ? 100 : progressValue);	
    }


    if (!this.maxTarget) {
      message = 'Please enter target amounts in Theme Settings > Cart Promotions'
      return;
    }

    if (cartTotal <= 0) {
      message = '';
      
    } else {
      for (const [index, target] of targets.entries()) {
        const promoDot = this.querySelector(`.promo--${target.name}-${index}`);
        const remaining = this.#convertToCurrency(target.value) - cartTotal;

        if (remaining <= 0) {
          messageSuccess = `<span>${target.messaging.success.replaceAll('[title]', target.title ?? '')}</span><br/>` ?? '';
          if (promoDot) promoDot.classList.add('active');
        } else {
          message += target.messaging.ongoing.replace('[remaining]', `${currencySymbol}${remaining ? remaining.toFixed(2) : target.value.toFixed(2)}`).replaceAll('[title]', target.title ?? '') ?? '';
          if (promoDot) promoDot.classList.remove('active');
          break;
        }
      }
    }

    // Display message
    let messageWrapper = document.getElementById('cart-promotions-message-tiers');
    messageWrapper.innerHTML = message;
    message ? messageWrapper.classList.add('show') : messageWrapper.classList.remove('show');

    let successMessageWrapper = document.getElementById('cart-promotions-message-success-tiers');
    successMessageWrapper.innerHTML = messageSuccess;
    messageSuccess ? successMessageWrapper.classList.add('show') : successMessageWrapper.classList.remove('show');

    // document.getElementById('cart-promotions-message-tiers').innerHTML = message;
    // document.getElementById('cart-promotions-message-success-tiers').innerHTML = messageSuccess;
  }
}

customElements.define('widget-cart-threshold-tiers', CartThresholdPromoTiers);